import React, { useState, useMemo, useEffect, useCallback, useContext } from 'react';
import {
  CalendarDaysIcon,
  PlusCircleIcon,
  ArrowPathIcon,
  TrashIcon,
  UserGroupIcon,
  ChartBarIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XMarkIcon,
  DocumentArrowDownIcon,
  TableCellsIcon,
  ExclamationTriangleIcon,
  BanknotesIcon,
  CreditCardIcon,
  PencilSquareIcon,
  CheckIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
  BoltIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import StatPill from '../components/v2/StatPill';
import TitleAction from '../components/v2/TitleAction';

const XLSX = require('xlsx');
const jsPDF = require('jspdf').jsPDF;
require('jspdf-autotable');

// Extend jsPDF type
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

interface Member {
  mg_nr: string;
  vorname: string;
  name: string;
  email?: string;
}

interface Event {
  id: number;
  beschriftung: string;
  date?: string;
}

interface Drink {
  id?: number;
  member_id: string;
  month: number;
  amount: number;
  paid: number;
  paid_date?: string;
  payment_method?: string;
}

const DrinksManagement: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [members, setMembers] = useState<Member[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [drinks, setDrinks] = useState<Drink[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // UI states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUnpaidOnly, setFilterUnpaidOnly] = useState(false);
  const [filterWithDrinksOnly, setFilterWithDrinksOnly] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'amount'>('name');
  const [quickAddMode, setQuickAddMode] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<number | null>(null);
  
  // Modals
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState('');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<{member: string, event: number} | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'paypal' | 'bank_transfer'>('cash');
  
  // Editing
  const [editingCell, setEditingCell] = useState<{member: string, event: number} | null>(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [membersRes, eventsRes, drinksRes] = await Promise.all([
        apiClient.get('/api/members'),
        apiClient.get('/api/events'),
        apiClient.get('/api/drinks'),
      ]);

      setMembers(membersRes.data || []);
      setEvents(eventsRes.data || []);
      setDrinks(drinksRes.data || []);
    } catch (err: any) {
      setMessage({ 
        type: 'error', 
        text: err.message || 'Fehler beim Laden der Daten.' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Create payments map for quick lookup
  const paymentsMap = useMemo(() => {
    const map: Record<string, Record<number, Drink>> = {};
    drinks.forEach(drink => {
      if (!map[drink.member_id]) map[drink.member_id] = {};
      map[drink.member_id][drink.month] = drink;
    });
    return map;
  }, [drinks]);

  // Filter and sort members
  const filteredMembers = useMemo(() => {
    let filtered = members.filter(member => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const fullName = `${member.vorname} ${member.name}`.toLowerCase();
        if (!fullName.includes(search) && !member.mg_nr.includes(search)) {
          return false;
        }
      }
      
      // Filter for members with drinks
      if (filterWithDrinksOnly) {
        const hasDrinks = drinks.some(d => d.member_id === member.mg_nr && d.amount > 0);
        if (!hasDrinks) return false;
      }
      
      // Filter for unpaid only
      if (filterUnpaidOnly) {
        const hasUnpaid = drinks.some(d => d.member_id === member.mg_nr && !d.paid && d.amount > 0);
        if (!hasUnpaid) return false;
      }
      
      return true;
    });

    // Sort
    if (sortBy === 'amount') {
      filtered.sort((a, b) => {
        const aTotal = drinks
          .filter(d => d.member_id === a.mg_nr && !d.paid)
          .reduce((sum, d) => sum + d.amount, 0);
        const bTotal = drinks
          .filter(d => d.member_id === b.mg_nr && !d.paid)
          .reduce((sum, d) => sum + d.amount, 0);
        return bTotal - aTotal;
      });
    } else {
      filtered.sort((a, b) => {
        const aName = `${a.vorname} ${a.name}`;
        const bName = `${b.vorname} ${b.name}`;
        return aName.localeCompare(bName);
      });
    }

    return filtered;
  }, [members, drinks, searchTerm, filterUnpaidOnly, filterWithDrinksOnly, sortBy]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalUnpaid = drinks
      .filter(d => !d.paid)
      .reduce((sum, d) => sum + d.amount, 0);
    
    const totalPaid = drinks
      .filter(d => d.paid)
      .reduce((sum, d) => sum + d.amount, 0);
    
    const membersWithDrinks = new Set(
      drinks.filter(d => d.amount > 0).map(d => d.member_id)
    ).size;
    
    const avgPerPerson = membersWithDrinks > 0 
      ? (totalUnpaid + totalPaid) / membersWithDrinks 
      : 0;
    
    const avgPerEvent = events.length > 0
      ? (totalUnpaid + totalPaid) / events.length
      : 0;

    return {
      totalUnpaid,
      totalPaid,
      membersWithDrinks,
      avgPerPerson,
      avgPerEvent,
    };
  }, [drinks, events]);

  // Event handlers
  const handleCreateEvent = async () => {
    if (!newEventTitle.trim()) return;
    
    try {
      const response = await apiClient.post('/api/events', {
        beschriftung: newEventTitle,
        date: new Date().toISOString(),
      });
      
      if (response.data) {
        setEvents([...events, response.data]);
        setNewEventTitle('');
        setEventModalOpen(false);
        setMessage({ type: 'success', text: 'Event erfolgreich erstellt!' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Fehler beim Erstellen des Events.' });
    }
  };

  const handleDeleteEvent = async (eventId: number) => {
    if (!window.confirm('Möchten Sie dieses Event wirklich löschen? Alle zugehörigen Einträge werden ebenfalls gelöscht.')) {
      return;
    }
    
    try {
      await apiClient.delete(`/api/events/${eventId}`);
      setEvents(events.filter(e => e.id !== eventId));
      setDrinks(drinks.filter(d => d.month !== eventId));
      setMessage({ type: 'success', text: 'Event erfolgreich gelöscht!' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Fehler beim Löschen des Events.' });
    }
  };

  const handleCellEdit = (memberId: string, eventId: number) => {
    const payment = paymentsMap[memberId]?.[eventId];
    setEditingCell({ member: memberId, event: eventId });
    setEditValue(payment?.amount?.toString() || '');
  };

  const handleCellSave = async () => {
    if (!editingCell) return;
    
    const amount = parseFloat(editValue) || 0;
    const payment = paymentsMap[editingCell.member]?.[editingCell.event];
    
    try {
      if (payment?.id) {
        // Update existing
        await apiClient.put(`/api/drinks/${payment.id}`, { amount });
        setDrinks(drinks.map(d => 
          d.id === payment.id ? { ...d, amount } : d
        ));
      } else if (amount > 0) {
        // Create new
        const response = await apiClient.post('/api/drinks', {
          member_id: editingCell.member,
          month: editingCell.event,
          amount,
          paid: 0,
        });
        if (response.data) {
          setDrinks([...drinks, response.data]);
        }
      }
      
      setEditingCell(null);
      setEditValue('');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Fehler beim Speichern.' });
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedPayment) return;
    
    const payment = paymentsMap[selectedPayment.member]?.[selectedPayment.event];
    if (!payment?.id) return;
    
    try {
      await apiClient.put(`/api/drinks/${payment.id}`, {
        paid: 1,
        paid_date: new Date().toISOString(),
        payment_method: paymentMethod,
      });
      
      setDrinks(drinks.map(d => 
        d.id === payment.id 
          ? { ...d, paid: 1, paid_date: new Date().toISOString(), payment_method: paymentMethod }
          : d
      ));
      
      setPaymentModalOpen(false);
      setSelectedPayment(null);
      setMessage({ type: 'success', text: 'Zahlung erfolgreich markiert!' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Fehler beim Markieren der Zahlung.' });
    }
  };

  const handleQuickAdd = async (memberId: string) => {
    if (!selectedEvent) return;
    
    const payment = paymentsMap[memberId]?.[selectedEvent];
    const newAmount = payment?.amount === 5 ? 0 : 5;
    
    try {
      if (payment?.id) {
        // Update existing
        await apiClient.put(`/api/drinks/${payment.id}`, { amount: newAmount });
        setDrinks(drinks.map(d => 
          d.id === payment.id ? { ...d, amount: newAmount } : d
        ));
      } else if (newAmount > 0) {
        // Create new
        const response = await apiClient.post('/api/drinks', {
          member_id: memberId,
          month: selectedEvent,
          amount: newAmount,
          paid: 0,
        });
        if (response.data) {
          setDrinks([...drinks, response.data]);
        }
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Fehler beim Speichern.' });
    }
  };

  // Export functions
  const exportToExcel = () => {
    const data: any[][] = [];
    
    // Header row
    const header = ['Name'];
    events.forEach(event => header.push(event.beschriftung));
    header.push('Gesamt Unbezahlt');
    data.push(header);
    
    // Data rows
    filteredMembers.forEach(member => {
      const row = [getMemberName(member.mg_nr)];
      
      events.forEach(event => {
        const payment = paymentsMap[member.mg_nr]?.[event.id];
        if (payment && payment.amount > 0) {
          row.push(payment.paid ? `${payment.amount} ✓` : String(payment.amount));
        } else {
          row.push('');
        }
      });
      
      const unpaidTotal = drinks
        .filter(d => d.member_id === member.mg_nr && !d.paid)
        .reduce((sum, d) => sum + d.amount, 0);
      row.push(unpaidTotal > 0 ? String(unpaidTotal) : '');
      
      data.push(row);
    });
    
    // Create workbook
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Verzehrkosten');
    
    // Save file
    XLSX.writeFile(wb, `Verzehrkosten_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    setMessage({ type: 'success', text: 'Excel-Export erfolgreich!' });
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF('landscape');
    
    // Title
    doc.setFontSize(16);
    doc.text('Verzehrkosten Übersicht', 14, 15);
    doc.setFontSize(10);
    doc.text(`Erstellt am: ${new Date().toLocaleDateString('de-DE')}`, 14, 22);
    
    // Prepare table data
    const tableColumn = ['Name', ...events.map(e => e.beschriftung), 'Unbezahlt'];
    const tableRows: any[][] = [];
    
    filteredMembers.forEach(member => {
      const row = [getMemberName(member.mg_nr)];
      
      events.forEach(event => {
        const payment = paymentsMap[member.mg_nr]?.[event.id];
        if (payment && payment.amount > 0) {
          row.push(payment.paid ? `${payment.amount} €` : `${payment.amount} €`);
        } else {
          row.push('');
        }
      });
      
      const unpaidTotal = drinks
        .filter(d => d.member_id === member.mg_nr && !d.paid)
        .reduce((sum, d) => sum + d.amount, 0);
      row.push(unpaidTotal > 0 ? `${unpaidTotal} €` : '');
      
      tableRows.push(row);
    });
    
    // Add table
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 30,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [220, 38, 38] },
    });
    
    // Save file
    doc.save(`Verzehrkosten_${new Date().toISOString().split('T')[0]}.pdf`);
    
    setMessage({ type: 'success', text: 'PDF-Export erfolgreich!' });
  };

  // Helper functions
  const getMemberName = (mg_nr: string) => {
    const member = members.find(m => m.mg_nr === mg_nr);
    return member ? `${member.vorname} ${member.name}` : mg_nr;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
            <div className="space-y-2 flex-1">
              <div className="h-6 w-52 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-4 w-36 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          </div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-3 w-1/3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
              <div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <TitleAction>
        <button
          onClick={() => setEventModalOpen(true)}
          className="hidden sm:flex h-11 px-4 items-center gap-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-semibold transition-colors"
        >
          <PlusCircleIcon className="h-5 w-5" />
          <span className="text-sm">Event</span>
        </button>
      </TitleAction>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4 lg:mb-6">
        {members.length > 0 && (
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Mitglied suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-11 pl-10 pr-10 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full"
              >
                <XMarkIcon className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
        )}
        <button
          onClick={exportToExcel}
          className="hidden md:flex w-11 h-11 items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          title="Excel Export"
        >
          <TableCellsIcon className="h-5 w-5" />
        </button>
        <button
          onClick={exportToPDF}
          className="hidden md:flex w-11 h-11 items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          title="PDF Export"
        >
          <DocumentArrowDownIcon className="h-5 w-5" />
        </button>
        <button
          onClick={fetchData}
          className="w-11 h-11 flex items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          title="Aktualisieren"
        >
          <ArrowPathIcon className="h-5 w-5" />
        </button>
        <button
          onClick={() => setEventModalOpen(true)}
          className="sm:hidden h-11 px-4 flex items-center gap-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-semibold transition-colors"
        >
          <PlusCircleIcon className="h-5 w-5" />
          <span className="text-sm">Event</span>
        </button>
      </div>

      {/* Stats (StatPill) */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-6">
        <StatPill
          value={stats.totalUnpaid > 999 ? `${(stats.totalUnpaid/1000).toFixed(1)}k` : stats.totalUnpaid > 99 ? `${Math.round(stats.totalUnpaid)}€` : formatCurrency(stats.totalUnpaid)}
          label="Offen"
          tone="danger"
        />
        <StatPill
          value={stats.totalPaid > 999 ? `${(stats.totalPaid/1000).toFixed(1)}k` : stats.totalPaid > 99 ? `${Math.round(stats.totalPaid)}€` : formatCurrency(stats.totalPaid)}
          label="Bezahlt"
          tone="success"
        />
        <StatPill value={stats.membersWithDrinks} label="Teilnehmer" tone="info" />
        <StatPill
          value={stats.avgPerPerson > 99 ? `${Math.round(stats.avgPerPerson)}€` : formatCurrency(stats.avgPerPerson)}
          label="Ø/Person"
          tone="neutral"
        />
        <StatPill
          value={stats.avgPerEvent > 99 ? `${Math.round(stats.avgPerEvent)}€` : formatCurrency(stats.avgPerEvent)}
          label="Ø/Event"
          tone="neutral"
        />
      </div>

      {/* Filters and Controls */}
      <div className="mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setFilterUnpaidOnly(!filterUnpaidOnly)}
              className={`px-3 py-2 text-sm rounded-lg font-medium transition-colors flex items-center gap-2 ${
                filterUnpaidOnly
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <FunnelIcon className="h-4 w-4" />
              Nur Unbezahlt
            </button>

            <button
              onClick={() => setFilterWithDrinksOnly(!filterWithDrinksOnly)}
              className={`px-3 py-2 text-sm rounded-lg font-medium transition-colors flex items-center gap-2 ${
                filterWithDrinksOnly
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <UserGroupIcon className="h-4 w-4" />
              Mit Beiträgen
            </button>

            <button
              onClick={() => setSortBy(sortBy === 'name' ? 'amount' : 'name')}
              className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-2 font-medium"
            >
              <ChartBarIcon className="h-4 w-4" />
              {sortBy === 'name' ? 'Nach Name' : 'Nach Betrag'}
            </button>

            {/* Quick Add Mode */}
            {quickAddMode && (
              <select
                value={selectedEvent || ''}
                onChange={(e) => setSelectedEvent(Number(e.target.value))}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">Event wählen</option>
                {events.map(event => (
                  <option key={event.id} value={event.id}>
                    {event.beschriftung}
                  </option>
                ))}
              </select>
            )}

            <button
              onClick={() => {
                setQuickAddMode(!quickAddMode);
                setSelectedEvent(null);
              }}
              className={`px-3 py-2 text-sm rounded-lg font-medium transition-colors flex items-center gap-2 ${
                quickAddMode
                  ? 'bg-warning-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <BoltIcon className="h-4 w-4" />
              Schnelleingabe
            </button>
          </div>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className="mb-6">
          <div
            className={`p-4 rounded-lg flex items-center gap-2 ${
              message.type === 'success' 
                ? 'bg-success-100 dark:bg-success-900/20 text-success-700 dark:text-success-400' 
                : 'bg-error-100 dark:bg-error-900/20 text-error-700 dark:text-error-400'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircleIcon className="h-5 w-5" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5" />
            )}
            <span className="flex-1">{message.text}</span>
            <button
              onClick={() => setMessage({ type: '', text: '' })}
              className="hover:opacity-70"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky left-0 bg-gray-50 dark:bg-gray-700 z-10">
                    Mitglied
                  </th>
                  {events.map(event => (
                    <th key={event.id} className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider min-w-[120px]">
                      <div className="flex items-center justify-center gap-2">
                        {event.beschriftung}
                        <button
                          onClick={() => handleDeleteEvent(event.id)}
                          className="p-1 hover:bg-error-100 dark:hover:bg-error-900/20 rounded transition-colors"
                        >
                          <TrashIcon className="h-4 w-4 text-error-500" />
                        </button>
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Unbezahlt
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredMembers.map((member) => {
                  const unpaidTotal = drinks
                    .filter(d => d.member_id === member.mg_nr && !d.paid)
                    .reduce((sum, d) => sum + d.amount, 0);
                  
                  return (
                    <tr key={member.mg_nr} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white sticky left-0 bg-white dark:bg-gray-800 z-10 border-r border-gray-200 dark:border-gray-700">
                        <div>
                          <div className="font-semibold">{member.vorname} {member.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">#{member.mg_nr}</div>
                        </div>
                      </td>
                      {events.map(event => {
                        const payment = paymentsMap[member.mg_nr]?.[event.id];
                        const isEditing = editingCell?.member === member.mg_nr && editingCell?.event === event.id;
                        
                        return (
                          <td key={event.id} className="px-4 py-3 text-center">
                            {quickAddMode && selectedEvent === event.id ? (
                              <button
                                onClick={() => handleQuickAdd(member.mg_nr)}
                                className={`w-full px-3 py-1 rounded-lg font-medium transition-colors ${
                                  payment?.amount === 5
                                    ? payment.paid 
                                      ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-400'
                                      : 'bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-400'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-warning-100 dark:hover:bg-warning-900/30'
                                }`}
                              >
                                {payment?.amount === 5 ? '5 €' : '+5 €'}
                              </button>
                            ) : isEditing ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleCellSave();
                                    if (e.key === 'Escape') setEditingCell(null);
                                  }}
                                  className="w-20 px-2 py-1 border border-primary-500 rounded text-center focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                                  autoFocus
                                  step="0.01"
                                />
                                <button
                                  onClick={handleCellSave}
                                  className="p-1 text-success-600 hover:bg-success-100 dark:hover:bg-success-900/20 rounded"
                                >
                                  <CheckIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => setEditingCell(null)}
                                  className="p-1 text-error-600 hover:bg-error-100 dark:hover:bg-error-900/20 rounded"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <div
                                onDoubleClick={() => handleCellEdit(member.mg_nr, event.id)}
                                className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-2 py-1"
                              >
                                {payment?.amount > 0 ? (
                                  <div className="flex items-center justify-center gap-1">
                                    <span className={`font-medium ${payment.paid ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                                      {payment.amount} €
                                    </span>
                                    {payment.paid ? (
                                      <CheckCircleIcon className="h-4 w-4 text-success-500" />
                                    ) : (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedPayment({ member: member.mg_nr, event: event.id });
                                          setPaymentModalOpen(true);
                                        }}
                                        className="p-1 hover:bg-primary-100 dark:hover:bg-primary-900/20 rounded transition-colors"
                                      >
                                        <CreditCardIcon className="h-4 w-4 text-primary-600" />
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-center">
                        {unpaidTotal > 0 ? (
                          <span className="px-3 py-1 bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-400 rounded-full font-bold">
                            {unpaidTotal} €
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Event Modal */}
      {eventModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <CalendarDaysIcon className="h-6 w-6 text-primary-600" />
                Neues Event erstellen
              </h3>
              <button
                onClick={() => setEventModalOpen(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Event-Name
              </label>
              <input
                type="text"
                value={newEventTitle}
                onChange={(e) => setNewEventTitle(e.target.value)}
                placeholder="z.B. Vereinsabend März"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                autoFocus
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setEventModalOpen(false)}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
              >
                Abbrechen
              </button>
              <button
                onClick={handleCreateEvent}
                disabled={!newEventTitle.trim()}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <PlusCircleIcon className="h-5 w-5" />
                Erstellen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {paymentModalOpen && selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <CreditCardIcon className="h-6 w-6 text-primary-600" />
                Zahlung markieren
              </h3>
              <button
                onClick={() => {
                  setPaymentModalOpen(false);
                  setSelectedPayment(null);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            
            <div className="mb-6 space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Mitglied</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {getMemberName(selectedPayment.member)}
                </p>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Betrag</p>
                <p className="text-2xl font-bold text-primary-600">
                  {formatCurrency(paymentsMap[selectedPayment.member]?.[selectedPayment.event]?.amount || 0)}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Zahlungsmethode
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value as any)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="cash">Bar</option>
                  <option value="paypal">PayPal</option>
                  <option value="bank_transfer">Überweisung</option>
                </select>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setPaymentModalOpen(false);
                  setSelectedPayment(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
              >
                Abbrechen
              </button>
              <button
                onClick={handleMarkPaid}
                className="flex-1 px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors font-medium flex items-center justify-center gap-2"
              >
                <CheckCircleIcon className="h-5 w-5" />
                Als bezahlt markieren
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrinksManagement;